/** Tiny hyperscript-style DOM helper — no framework, keeps the bundle small. */

type Child = Node | string | number | null | undefined | false;
type Props = Record<string, unknown>;

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(props)) {
    if (value == null || value === false) continue;
    if (key === 'class') el.className = String(value);
    else if (key === 'dataset') {
      for (const [d, v] of Object.entries(value as Record<string, string>)) el.dataset[d] = v;
    } else if (key.startsWith('on') && typeof value === 'function') {
      el.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else if (key === 'html') {
      el.innerHTML = String(value);
    } else if (key in el && key !== 'list' && key !== 'role') {
      // Prefer the DOM property when one exists (value, disabled, …).
      (el as unknown as Record<string, unknown>)[key] = value;
    } else {
      el.setAttribute(key, String(value));
    }
  }
  for (const child of children.flat()) {
    if (child == null || child === false) continue;
    el.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return el;
}

export function clear(node: HTMLElement): void {
  node.replaceChildren();
}

export function mount(node: HTMLElement, ...children: Child[]): void {
  clear(node);
  for (const c of children) if (c != null && c !== false) node.append(c instanceof Node ? c : document.createTextNode(String(c)));
}

/** Focus-trap + Escape-to-close for a modal element. Returns a disposer. */
export function trapFocus(modal: HTMLElement, onClose: () => void): () => void {
  const selector =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = Array.from(modal.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => !el.hasAttribute('disabled'),
    );
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  modal.addEventListener('keydown', onKey);
  return () => modal.removeEventListener('keydown', onKey);
}
